import { ArrowRight, ChevronLeft, ChevronRight, Plus, Users } from 'lucide-react'
import { Link } from 'react-router'
import { PeopleGroups } from '@/components/tree/PeopleViews'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import type { Circle } from '@/lib/families'
import type { MyPerson } from '@/lib/myPeople'
import { coverFaces, fromMyPeople, type ShownPerson } from '@/lib/peopleList'
import { cn } from '@/lib/utils'

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

// Your circles as a compact list, like the people on Connections: each row is a few faces from it, its name and how
// many are in the app, and opens on everyone there. The one you're in is marked.
export function CircleAlbums({
  circles,
  people,
  here,
  onOpen,
}: {
  circles: Circle[]
  people: MyPerson[]
  here: string
  onOpen: (circleId: string) => void
}) {
  return (
    <section aria-labelledby="your-circles" className="flex max-w-(--measure) flex-col gap-1">
      <h2 id="your-circles" className="text-sm font-semibold">
        Your circles
        <span className="ml-1.5 font-normal text-muted-foreground">{circles.length}</span>
      </h2>
      <ul className="-mx-2 flex flex-col">
        {circles.map((circle) => (
          <li key={circle.id}>
            <CircleRow
              circle={circle}
              faces={coverFaces(people, circle.id)}
              here={circle.id === here}
              onOpen={() => onOpen(circle.id)}
            />
          </li>
        ))}
        <li>
          <Link to="/circle/new" className={row}>
            <span className="grid size-11 shrink-0 place-items-center rounded-full border border-dashed text-muted-foreground">
              <Plus className="size-5" aria-hidden />
            </span>
            <span className="text-[0.9375rem] font-semibold">Create a circle</span>
          </Link>
        </li>
      </ul>
    </section>
  )
}

const row =
  'flex min-h-14 w-full items-center gap-3 rounded-xl px-2 py-1.5 text-left outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50'

function CircleRow({
  circle,
  faces,
  here,
  onOpen,
}: {
  circle: Circle
  faces: MyPerson[]
  here: boolean
  onOpen: () => void
}) {
  const count = faces.length
  return (
    <button type="button" onClick={onOpen} className={row}>
      {/* Up to four faces in one round, the way a group chat shows its people. */}
      <span
        aria-hidden
        className={cn(
          'grid size-11 shrink-0 gap-px overflow-hidden rounded-full bg-secondary',
          count > 1 && 'grid-cols-2',
          count > 2 && 'grid-rows-2',
          here && 'ring-2 ring-foreground ring-offset-2 ring-offset-background',
        )}
      >
        {count === 0 ? (
          <span className="grid place-items-center text-muted-foreground">
            <Users className="size-5" strokeWidth={1.75} />
          </span>
        ) : (
          faces.map((face, i) => (
            <Avatar
              key={face.key}
              person={{ id: face.key, name: face.name, portrait: face.portrait, pet: face.pet }}
              className={cn(
                'size-full rounded-none text-[0.625rem] shadow-none',
                count === 1 && 'text-base',
                count === 3 && i === 0 && 'row-span-2',
              )}
            />
          ))
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[0.9375rem] leading-snug font-semibold">
          {circle.name}
          {here && <span className="sr-only"> (you’re here)</span>}
        </span>
        <span className="truncate text-sub text-muted-foreground">
          {here ? 'You’re here · ' : ''}
          {plural(circle.members, 'person', 'people')} in the app
        </span>
      </span>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
    </button>
  )
}

// One circle's album: a back arrow, its name, a quick way into the circle, and everyone in it as the same list as
// Connections, each opening their profile.
export function CircleAlbum({
  circle,
  people,
  onBack,
  onOpenPerson,
  relation,
}: {
  circle: Circle
  people: MyPerson[]
  onBack: () => void
  onOpenPerson: (person: ShownPerson) => void
  relation?: (personId: string) => string
}) {
  const groups = fromMyPeople(people, 'name', circle.id, relation)
  const count = groups[0]?.people.length ?? 0
  return (
    <section aria-labelledby="album-name" className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="-ml-2 shrink-0" aria-label="All circles" onClick={onBack}>
          <ChevronLeft aria-hidden />
        </Button>
        <h2 id="album-name" className="min-w-0 flex-1 truncate text-title">
          {circle.name}
          <span className="ml-2 text-sub font-normal text-muted-foreground">{count}</span>
        </h2>
        <Button asChild size="sm" className="shrink-0 rounded-full pointer-coarse:min-h-11">
          <Link to={`/circle/${circle.id}`} aria-label={`Go to ${circle.name}`}>
            Go to circle <ArrowRight aria-hidden />
          </Link>
        </Button>
      </div>
      {count > 0 ? (
        <PeopleGroups groups={groups} circles={false} onOpen={onOpenPerson} />
      ) : (
        <p className="text-muted-foreground">No one here yet.</p>
      )}
    </section>
  )
}
